import React from 'react';
import { motion } from 'framer-motion';
import { Globe, ShieldCheck, Zap, BookOpen, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export const IndonesianFocusSection = () => {
  const { t } = useLanguage();
  const lang = t('indonesianFocus');

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-zinc-900 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4 block">
            {lang.eyebrow}
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-zinc-100 tracking-tight mb-6"
          >
            {lang.headline} <span className="gradient-text">{lang.headlineHighlight}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400"
          >
            {lang.subheadline}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Card 1 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-zinc-950/5 rounded-full blur-3xl" />
            < ShieldCheck className="w-10 h-10 text-zinc-100 mb-6" />
            <h3 className="text-xl font-semibold text-zinc-100 mb-3">
              {lang.card1.title}
            </h3>
            <p className="text-zinc-400 leading-relaxed">
              {lang.card1.description}
            </p>
          </motion.div>

          {/* Card 2 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-zinc-950/5 rounded-full blur-3xl" />
            <Zap className="w-10 h-10 text-zinc-100 mb-6" />
            <h3 className="text-xl font-semibold text-zinc-100 mb-3">
              {lang.card2.title}
            </h3>
            <p className="text-zinc-400 leading-relaxed">
              {lang.card2.description}
            </p>
          </motion.div>
        </div>

        {/* Founder Research Callout */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-8 p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 overflow-hidden"
        >
          <div className="flex items-start sm:items-center gap-3">
            <div className="hidden sm:flex w-8 h-8 rounded-full bg-zinc-800/50 flex-shrink-0 items-center justify-center border border-zinc-700/50">
              <BookOpen className="w-4 h-4 text-zinc-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-200 flex items-center gap-2 mb-1">
                {lang.research.badge}
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-md bg-zinc-800/60 text-zinc-400 border border-zinc-700/50">{lang.research.published}</span>
              </p>
              <p className="text-xs text-zinc-500 italic max-w-2xl">
                {lang.research.paperTitle}
              </p>
            </div>
          </div>
          <a
            href="https://ieeexplore.ieee.org/document/11330949"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-100 transition-colors whitespace-nowrap mt-2 sm:mt-0"
          >
            {lang.research.reviewLink} <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>

      </div>
    </section>
  );
};
